/**
 * Fetches CSRF token from the server's /api/csrf endpoint
 *
 * Retrieves a masked CSRF token that implements the double-submit cookie pattern.
 * The server sets an HTTP-only cookie and returns a response token that must be
 * included in later API requests for CSRF protection.
 *
 * Uses 'same-origin' credentials to ensure the CSRF cookie is included in the request.
 * This is essential for the double-submit cookie pattern to work correctly.
 *
 * @returns {Promise<string>} CSRF token to be used in X-CSRF-Token headers
 * @throws {Error} When the CSRF endpoint is unavailable or returns an error
 *
 * @example
 * ```javascript
 * const csrfToken = await fetchCSRFToken();
 *
 * // Use in later API calls
 * fetch('/api/protected-endpoint', {
 *   method: 'POST',
 *   headers: {
 *     'X-CSRF-Token': csrfToken,
 *     'Content-Type': 'application/json'
 *   },
 *   body: JSON.stringify(data)
 * });
 * ```
 */
export const fetchCSRFToken = async () => {
  const response = await fetch('/api/csrf', {
    method: 'GET',
    credentials: 'same-origin', // Ensure cookies are sent with the request
  });

  if (!response.ok) {
    throw new Error(`Error ${response.status}: Failed to fetch CSRF token`);
  }

  const data = await response.json();
  return data.csrfToken;
};

/**
 * Posts form data to SugarCRM via the legacy ISM API endpoint
 *
 * Handles secure form submission to SugarCRM with comprehensive protection:
 * - CSRF token validation using double-submit cookie pattern
 * - Cloudflare Turnstile bot protection verification
 * - Proper error handling and status code validation
 *
 * This function is used for legacy form submissions and will fetch a fresh
 * CSRF token for each request to ensure security.
 *
 * @param {Object} formData - Form data object to be sent to SugarCRM
 * @param {string} turnstileToken - Cloudflare Turnstile response token for bot protection
 * @returns {Promise<Object>} Response data from SugarCRM API
 * @throws {Error} When CSRF token fetch fails or SugarCRM API returns an error
 *
 * @example
 * ```javascript
 * const formData = {
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   email: 'john@example.com',
 *   // ... other form fields
 * };
 *
 * try {
 *   const result = await postToSugarCRM(formData, turnstileToken);
 *   console.log('Form submitted successfully:', result);
 * } catch (error) {
 *   console.error('Form submission failed:', error.message);
 * }
 * ```
 */
export const postToSugarCRM = async (formData, turnstileToken) => {
  // Get CSRF token
  const csrfToken = await fetchCSRFToken();

  const response = await fetch('/api/ism', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
      'cf-turnstile-response': turnstileToken,
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    throw new Error(`Error ${response.status}: Failed to send to sugarcrm`);
  }

  return await response.json();
};

/**
 * Posts form data to SugarCRM via the new CRM API endpoint (embedded form)
 *
 * Enhanced version of the SugarCRM integration with improved security and features.
 * Implements the same security measures as the legacy version but uses the updated
 * /api/crm endpoint for better performance and additional functionality.
 *
 * Security features:
 * - CSRF protection with double-submit cookie pattern
 * - Cloudflare Turnstile bot protection
 * - Secure header handling and error abstraction
 *
 * @param {Object} formData - Form data object to be sent to SugarCRM
 * @param {string} turnstileToken - Cloudflare Turnstile response token for bot protection
 * @returns {Promise<Object>} Response data from SugarCRM API
 * @throws {Error} When CSRF token fetch fails or SugarCRM API returns an error
 *
 * @example
 * ```javascript
 * const leadData = {
 *   firstName: 'Jane',
 *   lastName: 'Smith',
 *   email: 'jane@example.com',
 *   phone: '+1234567890',
 *   source: 'website-contact-form'
 * };
 *
 * try {
 *   const result = await postToSugarCRMNew(leadData, turnstileToken);
 *   console.log('Lead created successfully:', result);
 * } catch (error) {
 *   console.error('Lead creation failed:', error.message);
 * }
 * ```
 */
export const postToSugarCRMNew = async (formData, turnstileToken) => {
  // Get CSRF token
  const csrfToken = await fetchCSRFToken();

  const response = await fetch('/api/crm', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrfToken,
      'cf-turnstile-response': turnstileToken,
    },
    body: JSON.stringify(formData),
  });

  if (!response.ok) {
    throw new Error(`Error ${response.status}: Failed to send to sugarcrm`);
  }

  return await response.json();
};
